import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  HostStats,
  KernelEvent,
  ModeDefinition,
  RunBundle,
  RuntimeConfig,
} from "@picnic-field/local-host/protocol";
import type { Palette, PaletteSet } from "@picnic-field/renderer/appearance";
import { latestSnapshot, SnapshotRejected } from "../../../lib/api/fieldApi";
import type { Snapshot } from "../../../lib/api/fieldApi";
import { useBenchHandoff } from "../../../hooks/useBenchHandoff";
import { clearPendingReturn, peekPendingReturn } from "../../../lib/bench/handoff";
import type { BenchReturn } from "../../../lib/bench/handoff";
import { visibleImports } from "../../upload";
import { isStageMachine } from "../../../lib/stageMachine";
import { isFeCode, toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { useSocialApi } from "../../../lib/api/socialApi";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { HubHud } from "../../../components/HubHud";
import { useHubView } from "../../../hooks/useHubView";
import { PicoBubble, usePicoToast } from "../../../components/PicoBubble";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { feText } from "../../../i18n/fe";
import { glbUrlFor } from "../../../lib/preview";
import { usePrimeHub } from "../../../lib/kitVariant";
import type { LatencyProbe, SenderStats } from "../../remote";
import { RemoteOverlay } from "../../remote";
import { useRemote } from "../../remote";
import { COURSE_MODE_ID, useCourseApi } from "../../../lib/api/courseApi";
import { CourseScene } from "./CourseScene";
import {
  CoursePickDialog,
  CourseStrip,
  GhostPicker,
  MissionPickDialog,
  ReadyPane,
  ResultPane,
} from "./CoursePanels";
import type { ResultView, CourseViewLike, GhostCandidate, GhostLoad, HintRule } from "../types";
import { courseDeps } from "../lib/deps";
import { hasProgram } from "../lib/fakeHost";
import { ghostOf, toggleGhost } from "../lib/ghosts";
import { flushOutbox, retryPending, saveRun } from "../lib/replaySave";
import { failAnalysisOf, lastHint, pickHint, readHintRules, rememberHint } from "../lib/hints";
import type { PlaySettings } from "../../../lib/api/courseApi";
import { usePico } from "../../pico";
import { track } from "../../../lib/telemetry/telemetry";
import { usePerfTelemetry } from "../../../hooks/useTelemetry";
import { formatClock, progressCells } from "../lib/progress";
import { presetSummary, presetTitle } from "../lib/presets";
import { useCourseRun } from "../hooks/useCourseRun";
import { PrincipleButton, PrincipleLegend, useHostPrinciple } from "./Principle";
import { MissionCard, MissionSummary } from "./MissionPanels";
import { useMissionApi } from "../../../lib/api/missionApi";
import type { Mission } from "../../../lib/api/missionApi";
import {
  bandAfterPoll,
  bandAfterSave,
  bandAtFinish,
  hudGoalMs,
  missionPreset,
  POLL_MAX,
  POLL_MS,
} from "../lib/mission";
import "../../../styles/course.css";
import { staticReason } from "../../../lib/staticReason";
import type { StaticReason } from "../../../lib/staticReason";

/**
 * FS-04 장애물 코스(/course) — FS-04-01 코스 고르기 → FS-04-02 코스 달리기(FS-09 리모컨) → FS-04-03 결과.
 * 와이어(SCR v0.6 sfs-04 SE · sfs-04-su SU): 상단 바 '⛰ 코스 도전 · SE 모드 · 보통 · 계단+언덕',
 * 왼쪽 = 코스 띠 + 3D 장면, 오른쪽 = 준비/결과 창. 달리는 동안은 장면이 화면을 다 쓴다(FS-09 와이어 '코스 위 오버레이').
 * 점수·순위·비교 0(DR-01) · 자유 입력 0(DR-02) · SE 터치 타깃 ≥64/96px(DR-03).
 */
type Screen = "select" | "driving" | "result";

interface Data {
  config: RuntimeConfig;
  mode: ModeDefinition;
  snapshot: Snapshot | null;
  bundle: RunBundle | null;
  palette: PaletteSet;
  hints: HintRule[];
  /** S5 놀이 설정(현실감 · 교사 기본 프리셋 · 시간 예산) */
  play: PlaySettings;
  /** run-bundle 거절(제어 평면): 409 FE-204 · 409 FE-201 · 503 FE-201 · 403 FE-302 */
  bundleError: { code: string; status: number } | null;
}

const DEFAULT_PRESET = "medium";

/** 코스 도전 장면(2026-10-07) — 선 따라가기 · 걷기 · 그 밖(장애물)은 놀이공원 잔디 경주장 */
export const courseSceneOf = (presetKey: string): string =>
  presetKey.startsWith("line")
    ? "course-line"
    : presetKey.startsWith("walk")
      ? "course-walk"
      : "course-park";

declare global {
  interface Window {
    /** 개발·E2E·PT-02 계측(가짜 모드 · 개발 서버에서만) */
    __pfCourse?: {
      latency: () => ReturnType<LatencyProbe["summary"]>;
      hostStats: () => Promise<HostStats | null>;
      sender: () => SenderStats;
      view: () => ReturnType<CourseViewLike["stats"]> | null;
      state: () => {
        screen: string;
        phase: string;
        stalls: string[];
        attempts: number;
        realism: boolean;
        presetKey: string;
        failPattern: string | null;
      };
    };
  }
}

export function CourseScreen() {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const navigate = useNavigate();
  /** Campus 미션 입장 · 홈 '미션 이어 하기'로 왔는가(EntryScreen state.mission) */
  const fromMission = !!(useLocation().state as { mission?: boolean } | null)?.mission;
  const fieldApi = useFieldApi();
  const courseApi = useCourseApi();
  const social = useSocialApi();
  const toast = usePicoToast();
  const pico = usePico();
  /** 학생이 코스 카드를 직접 골랐는가(골랐으면 교사 기본 프리셋으로 되돌리지 않는다) */
  const presetChosen = useRef(false);
  // 4.6 §4.3 Campus 미션(Field 판정) — 묶음이 있으면 카드 · 목표 코스 미리 고르기 · HUD 목표 시간 · 결과 띠
  const missionApi = useMissionApi();
  const [mission, setMission] = useState<Mission | null>(null);
  const [missionSeq, bumpMission] = useReducer((x: number) => x + 1, 0);
  const missionApplied = useRef(false);

  const [data, setData] = useState<Data | null>(null);
  const prime = usePrimeHub(data?.snapshot?.glbVersion);   // 스파이크 프라임 판이면 허브 이름도 그 교구로
  const [loadError, setLoadError] = useState<ErrorCode | null>(null);
  const [loadSeq, setLoadSeq] = useState(0);
  const [presetKey, setPresetKey] = useState(DEFAULT_PRESET);
  const [candidates, setCandidates] = useState<GhostCandidate[]>([]);
  const [candSeq, setCandSeq] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [tracks, setTracks] = useState<GhostLoad[]>([]);
  const [ghostPalette, setGhostPalette] = useState<PaletteSet>(() => new Map());
  /** tracks 가 어느 고르기(selected)로 불러온 것인가 — 다 불러오기 전에는 출발하지 않는다(고스트 없이 출발 방지) */
  const [tracksKey, setTracksKey] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  /** 코스 고르기 · 미션 고르기 대화상자(2026-10-09 — 둘을 나눴다) */
  const [coursePick, setCoursePick] = useState(false);
  const [missionPick, setMissionPick] = useState(false);
  const [screen, setScreen] = useState<Screen>("select");
  const [pauseOpen, setPauseOpen] = useState(false);
  const [resultView, setResultView] = useState<ResultView | null>(null);
  const [respawnNote, setRespawnNote] = useState<"fall" | "reset" | null>(null);
  const viewRef = useRef<CourseViewLike | null>(null);
  const saveSeq = useRef(0);
  const ghostSwitched = useRef(new Set<string>());

  // ── 데이터: 런타임 설정 · 모드 · 내 스냅샷 · run-bundle · 팔레트 · 힌트 카탈로그 ──
  useEffect(() => {
    if (!fieldApi || !courseApi) return;
    let cancelled = false;
    setLoadError(null);
    (async () => {
      const [config, mode, list, hints, play] = await Promise.all([
        courseApi.runtimeConfig(),
        courseApi.mode(COURSE_MODE_ID),
        fieldApi.mySnapshots(),
        courseApi.catalog("hint_rule").then(readHintRules, () => []),
        courseApi.playSettings(),
      ]);
      const snapshot = latestSnapshot(list);
      // 내 작품 제 appearance.palette 이름의 팔레트(2026-09-30 팔레트 버전 전환 — 옛 brick-v1 작품은 brick-v1 색)
      const palette = await fieldApi.palettes([snapshot?.appearance?.palette]);
      let bundle: RunBundle | null = null;
      let bundleError: Data["bundleError"] = null;
      if (snapshot)
        try {
          bundle = await courseApi.runBundle(snapshot.snapshotId);
        } catch (e) {
          const err = toApiError(e);
          if (err.status === 0 || (err.status >= 500 && err.code !== "FE-201")) throw e;
          const raw = (err.body as { code?: unknown } | null)?.code;
          bundleError = { code: typeof raw === "string" ? raw : err.code, status: err.status };
        }
      if (cancelled) return;
      // FS-04-01 기본 코스 = 교사가 고른 프리셋(없거나 모르는 키면 보통)
      const teacherPreset = play.presetKey;
      if (
        !presetChosen.current &&
        teacherPreset &&
        mode.definition.presets.some((p) => p.key === teacherPreset)
      )
        setPresetKey(teacherPreset);
      setData({
        config,
        mode: mode.definition,
        snapshot,
        bundle,
        palette,
        hints,
        play,
        bundleError,
      });
    })().catch((e) => {
      if (!cancelled) setLoadError(toApiError(e).code);
    });
    return () => {
      cancelled = true;
    };
  }, [fieldApi, courseApi, loadSeq]);

  // 미션으로 왔는데 내 기계가 정적 기계(놀이기구 · 오토마타)면 무대로 — 코스 미션이어도 코스를 달릴 수 없다(운영 7630 · 2026-10-06)
  useEffect(() => {
    if (fromMission && isStageMachine(data?.snapshot ?? null))
      navigate("/stage", { replace: true });
  }, [fromMission, data, navigate]);

  // ── 4.6 미션 묶음(없으면 null — 미션 없이도 놀이는 그대로) ──
  useEffect(() => {
    if (!missionApi) return;
    let cancelled = false;
    void missionApi.mission().then((m) => {
      if (cancelled) return;
      // 기구 무대 미션(정적 기계 · 2026-10-06)만 있으면 무대로 — Campus 입장은 next=course 로 온다
      if (m?.goals.length && m.goals.every((g) => g.modeId === "machine-stage")) {
        navigate("/stage", { replace: true });
        return;
      }
      setMission(m);
      // 처음 한 번 — 학생이 아직 코스를 고르지 않았으면 미션 코스를 미리 고른다(교사 기본 프리셋보다 먼저)
      const p = missionPreset(m, COURSE_MODE_ID);
      if (p && !missionApplied.current && !presetChosen.current) {
        missionApplied.current = true;
        presetChosen.current = true;
        setPresetKey(p);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [missionApi, missionSeq, navigate]);

  // ── 'Bench 에서 고치기'(Sprint 4 · SR-INT-02) — 내 스냅샷의 Bench 프로젝트로 입장권 · 돌아오면 그 저장본으로 다시 올린다 ──
  const feToast = useCallback(
    (code: ErrorCode) => {
      const { cause, next } = feText(t, code, "student");
      toast.show(`course-${code}`, [cause, next].filter(Boolean).join(" "));
    },
    [t, toast],
  );
  const [returnSeq, bumpReturn] = useReducer((x: number) => x + 1, 0);
  const handoff = useBenchHandoff(fieldApi, () => {
    toast.show("course-bench-back", t("course.benchBack"));
    bumpReturn();
  });
  const benchRoot = import.meta.env.VITE_BENCH_URL || null;
  const benchProject = data?.snapshot?.programProjectId ?? null;
  /**
   * Bench 열기 — 프로젝트를 알면 입장권(돌아오는 길이 있다), 모르면(프로그램 없음 · 옛 제어 평면) 예전처럼 Bench 첫 화면을 새 탭(noopener).
   * @returns 무엇이든 열었으면 true(Bench 가 설정되지 않았으면 false)
   */
  const openBench = useCallback((): boolean => {
    if (benchProject && handoff.available) {
      handoff.open(benchProject).then(
        (ok) => {
          if (!ok) feToast("FE-201");
        },
        (e) => {
          const code = toApiError(e).code;
          feToast(isFeCode(code) ? code : "FE-201");
        },
      );
      return true;
    }
    if (!benchRoot) return false;
    window.open(benchRoot, "_blank", "noopener");
    return true;
  }, [benchProject, handoff, benchRoot, feToast]);

  // 돌아온 저장본 → 같은 조립 + 그 저장본으로 다시 올리고(createSnapshot 고정) run-bundle 을 다시 받는다
  const [reimporting, setReimporting] = useState(false);
  const reimport = async (pin: BenchReturn) => {
    if (!fieldApi) return;
    const projectId = pin.projectId ?? data?.snapshot?.programProjectId ?? null;
    // 어느 프로젝트인지 모르면 여기서 쓰지 않는다(맡긴 채로 — 룸 · 올리기 화면이 쓸 수 있다)
    if (!projectId) return;
    clearPendingReturn();
    setReimporting(true);
    try {
      const src = await fieldApi.sources();
      const ready = visibleImports(src.campus).filter((c) => c.status === "ready");
      const slug = data?.snapshot?.modelSlug;
      const imp = ready.find((c) => c.modelSlug === slug) ?? ready[0] ?? null;
      if (!imp) {
        feToast("FE-101");
        return;
      }
      await fieldApi.createSnapshot({
        importId: imp.importId,
        benchProjectId: projectId,
        benchVersionHash: pin.benchVersionHash,
      });
      setResultView(null);
      setPauseOpen(false);
      setScreen("select");
      setLoadSeq((n) => n + 1);
      toast.show("course-bench-reuploaded", t("course.benchReuploaded"));
    } catch (e) {
      if (e instanceof SnapshotRejected) feToast(e.code);
      else {
        const code = toApiError(e).code;
        feToast(isFeCode(code) ? code : "FE-201");
      }
    } finally {
      setReimporting(false);
    }
  };
  // 같은 탭 postMessage · 폴백(다시 들어온 뒤 localStorage) 모두 — 달리는 중이 아니고 화면이 준비되면 한 번
  const dataReady = !!data;
  useEffect(() => {
    if (!fieldApi || !dataReady || screen === "driving" || reimporting) return;
    const pin = peekPendingReturn();
    if (pin) void reimport(pin);
    // reimport 는 매 렌더 새로 만들어진다 — 조건이 바뀔 때만 다시 본다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldApi, dataReady, screen, reimporting, returnSeq]);

  const presets = data?.mode.presets ?? [];
  const preset = presets.find((p) => p.key === presetKey) ?? presets[0];
  const maxGhosts = data?.config.local.maxGhosts ?? 3;
  const movable = data?.bundle ? hasProgram(data.bundle) : false;
  // 움직이지 않는 이유(옆 안내 한 줄) — 스냅샷이 정적이면 board 로, 아니면(프로그램 없는 번들) 프로그램 없음
  const staticWhy: StaticReason = data?.snapshot?.static
    ? staticReason(data.snapshot.board)
    : "noProgram";

  useTopBar({
    icon: "⛰",
    title: t("course.title"),
    chips: preset
      ? [
          {
            label: [presetTitle(t, preset), presetSummary(t, preset)].filter(Boolean).join(" · "),
            testId: "course-chip",
          },
        ]
      : [],
  });

  // ── 고스트 후보(프리셋마다) ──
  useEffect(() => {
    if (!social || !courseApi) return;
    let cancelled = false;
    courseDeps()
      .ghostSource({
        social,
        runBundle: (id) => courseApi.runBundle(id),
        modeId: COURSE_MODE_ID,
        mineLabel: (n) => t("course.ghost.localName", { n }),
      })
      .then((src) => src.list(presetKey))
      .then((list) => {
        if (cancelled) return;
        setCandidates(list);
        setSelected((sel) => sel.filter((id) => list.some((g) => g.ghostId === id)));
      })
      .catch(() => !cancelled && setCandidates([]));
    return () => {
      cancelled = true;
    };
  }, [presetKey, candSeq, t, social, courseApi]);

  // 고른 고스트의 트랙
  useEffect(() => {
    let cancelled = false;
    const chosen = selected
      .map((id) => candidates.find((g) => g.ghostId === id))
      .filter((g): g is GhostCandidate => !!g);
    Promise.all(chosen.map((g) => g.load().catch(() => null))).then(async (ts) => {
      const ok = ts.filter((x): x is GhostLoad => !!x);
      // 고스트도 제 appearance.palette 이름의 팔레트로 — 받은 뒤에 준비 완료(ghostsReady)
      const pal = fieldApi
        ? await fieldApi.palettes(ok.map((g) => ghostOf(g).bundle.appearance?.palette))
        : new Map<string, Palette>();
      if (cancelled) return;
      setGhostPalette(pal);
      setTracks(ok);
      setTracksKey(selected.join("|"));
    });
    return () => {
      cancelled = true;
    };
  }, [selected, candidates, fieldApi]);

  // 고른 고스트를 다 불러와 커널에 준비했는가(준비 load 는 tracks 가 바뀔 때 다시 돈다)
  const ghostsReady = tracksKey === selected.join("|");
  /** 장면 팔레트 묶음 = 내 작품 + 고른 고스트(이름별 — 작품마다 제 팔레트) */
  const myPalette = data?.palette;
  const scenePalette = useMemo<PaletteSet>(
    () => new Map([...(myPalette ?? []), ...ghostPalette]),
    [myPalette, ghostPalette],
  );

  // ── 커널 호스트 ──
  // 허브 출력(SR-RUN-03 — 가운데 불빛 · 5×5 화면 · 소리)을 HUD 에
  const { hub: hubView, now: hubNow, onEvent: onHubOut } = useHubView();
  const onEvent = useCallback(
    (e: KernelEvent) => {
      onHubOut(e);
      if (e.type === "fe" && (e.code === "FE-202" || e.code === "FE-203")) {
        // FS-C1-07 — 학생에게는 피코 토스트(코드 없음), 같은 안내는 한 번
        const code = e.code as ErrorCode;
        const { cause, next } = feText(t, code, "student");
        toast.show(`course-${code}`, [cause, next].filter(Boolean).join(" "));
        track({ t: "fe", code, detail: "course" });
      }
      // 리플레이 고스트가 위치 트랙으로 넘어감(FE-501) — 학생에게는 보이지 않고 검증 결과로만 보고한다
      if (e.type === "playback" && e.mode === "track" && !ghostSwitched.current.has(e.owner)) {
        ghostSwitched.current.add(e.owner);
        track({ t: "playback", code: "track", detail: "ghost" });
      }
      if (e.type === "fe" && e.code === "FE-501" && e.detail) ghostSwitched.current.add(e.detail);
      if (e.type === "respawn") setRespawnNote(e.reason);
    },
    [t, toast, onHubOut],
  );
  const run = useCourseRun({
    bundle: data?.bundle ?? null,
    mode: data?.mode ?? null,
    config: data?.config ?? null,
    realism: data?.play.realism ?? false,
    onEvent,
    onGhostError: () => toast.show("course-ghost-unreadable", t("course.ghost.unreadable")),
  });
  // P2-S5 원리 보기(SR-PHY-07) — 달리는 화면에서 켠다(SE 화살표만 · SU 수치)
  const [principleOn, setPrincipleOn] = useState(false);
  const principle = screen === "driving" && principleOn;
  useHostPrinciple(run.host, principle, run.load);

  // 고르는 동안: 프리셋·고스트가 바뀌면 코스를 다시 준비(장면 미리보기)
  const hostReady = run.status !== "creating" && run.status !== "error";
  useEffect(() => {
    if (!data?.bundle || !hostReady || screen !== "select") return;
    void run.prepare(presetKey, tracks);
    // run.prepare 는 bundle·mode·config 가 같으면 같은 함수
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, hostReady, presetKey, tracks, screen === "select"]);

  // 리스폰 안내는 잠깐만
  useEffect(() => {
    if (!respawnNote) return;
    const h = setTimeout(() => setRespawnNote(null), 2500);
    return () => clearTimeout(h);
  }, [respawnNote, run.run.respawn?.seq]);

  // 판 끝 → 결과(FS-04-03): 힌트 1개 · 리플레이 내보내기 → 올리기 → 도전 기록(SR-REC-01)
  useEffect(() => {
    if (run.run.phase !== "over" || !run.run.result || screen !== "driving" || !data) return;
    const r = run.run.result;
    // 실패 분석(커널) → 규칙 힌트 1개(같은 힌트 연속 금지) · 멈춘 곳(SU)
    const analysis = failAnalysisOf({ result: r, analysis: run.run.analysis });
    const rule = analysis ? pickHint(analysis.pattern, data.hints, lastHint()) : null;
    rememberHint(rule?.key ?? null);
    const reaction = pico.react(r.completed ? "course-finish" : "course-fail", { toast: false });
    viewRef.current?.stage?.react(r.completed ? "cheer" : "oops");
    const seq = ++saveSeq.current;
    const where =
      analysis && analysis.progress > 0
        ? { surface: analysis.surface, progress: analysis.progress }
        : run.run.best;
    // 4.6 FS-04-03 미션 띠 — 이 코스에 미션이 있고 완주했을 때만(미완주는 띠 없음 · DR-01)
    const missionView = bandAtFinish(mission, COURSE_MODE_ID, presetKey, r.completed, r.timeMs);
    setResultView({
      mission: missionView,
      result: r,
      stoppedAt: r.completed
        ? null
        : { surface: where.surface, pct: Math.round(where.progress * 100) },
      hint: rule,
      failPattern: analysis?.pattern ?? null,
      pico: reaction,
      save: { state: "saving", replayId: null, pendingId: null },
    });
    setPauseOpen(false);
    setScreen("result");
    const snapshotId = data.snapshot?.snapshotId ?? "";
    // 고스트 재생 검증 결과(PATCH …/replays/{id} {hashOk}) — 서버 리플레이만(보관함 주행 제외)
    for (const g of tracks)
      if (g.kind === "replay" && !g.replay.ghostId.startsWith("pending-"))
        void social?.reportHash(g.replay.ghostId, !ghostSwitched.current.has(g.replay.ghostId));
    void (async () => {
      const exp = await run.exportReplay();
      const saved = social
        ? await saveRun(
            social,
            exp,
            {
              snapshotId,
              modeId: COURSE_MODE_ID,
              presetKey,
              outcome: r.completed ? "complete" : "incomplete",
              durationMs: Math.round(r.timeMs),
              tries: r.attempts,
              failPattern: analysis?.pattern ?? null,
            },
            data.bundle,
          )
        : ({ state: "failed", replayId: null, pendingId: null } as const);
      if (seq !== saveSeq.current) return;
      // 저장됨 → 피코 반응 replay-saved(저장 배너의 표정 — 토스트는 결과 버튼을 가려서 쓰지 않는다)
      const savedPico =
        saved.state === "saved" ? pico.react("replay-saved", { toast: false }) : undefined;
      setResultView((v) =>
        v
          ? {
              ...v,
              save: saved,
              savedPico,
              mission: v.mission ? bandAfterSave(v.mission, saved) : v.mission,
            }
          : v,
      );
      setCandSeq((n) => n + 1);
    })();
  }, [run, screen, data, presetKey, social, tracks, pico, mission]);

  // 4.6 미션 확인 조회(서버 재생 검증 — 3초마다 · 약 2분까지. 넘으면 '확인 중' 그대로 + 안내) · 끝나면 카드 상태를 다시 받는다
  const pollAttempt =
    resultView?.mission?.state === "pending" && !resultView.mission.slow
      ? (resultView.mission.attemptId ?? null)
      : null;
  useEffect(() => {
    if (!pollAttempt || !missionApi) return;
    let n = 0;
    let stopped = false;
    const h = setInterval(() => {
      n += 1;
      if (n > POLL_MAX) {
        clearInterval(h);
        setResultView((v) => (v?.mission ? { ...v, mission: { ...v.mission, slow: true } } : v));
        return;
      }
      missionApi.verification(pollAttempt).then(
        (r) => {
          if (stopped) return;
          setResultView((v) =>
            v?.mission && v.mission.attemptId === pollAttempt
              ? { ...v, mission: bandAfterPoll(v.mission, r) }
              : v,
          );
          if (r.status !== "pending") bumpMission();
        },
        () => {
          /* 잠깐 끊김 — 다음 차례에 다시 */
        },
      );
    }, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(h);
    };
  }, [pollAttempt, missionApi]);

  // 못 올린 주행(오프라인 보관함)은 화면에 들어올 때 · 연결이 돌아올 때 다시 올린다
  useEffect(() => {
    if (!social) return;
    const flush = () =>
      void flushOutbox(social).then((n) => {
        if (n > 0) setCandSeq((x) => x + 1);
        setResultView((v) =>
          v && (v.save.state === "offline" || v.save.state === "failed") && v.save.pendingId
            ? {
                ...v,
                save: { ...v.save, state: "saved" },
                // 보관함에서 올린 판은 확인 조회 id 를 모른다 — 띠는 거두고 카드(목표 상태)가 대신 알린다
                mission:
                  v.mission?.state === "unsaved" ? { ...v.mission, state: "none" } : v.mission,
              }
            : v,
        );
      });
    flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [social]);

  const retrySave = useCallback(async () => {
    const id = resultView?.save.pendingId;
    if (!social || !id) return;
    const seq = saveSeq.current;
    setResultView((v) => (v ? { ...v, save: { ...v.save, state: "saving" } } : v));
    const r = (await retryPending(social, id)) ?? {
      state: "saved" as const,
      replayId: null,
      pendingId: null,
    };
    if (seq !== saveSeq.current) return;
    setResultView((v) =>
      v
        ? {
            ...v,
            save: r,
            mission:
              v.mission?.state === "unsaved"
                ? bandAfterSave({ ...v.mission, state: "saving" }, r)
                : v.mission,
          }
        : v,
    );
    setCandSeq((n) => n + 1);
    bumpMission();
  }, [social, resultView?.save.pendingId]);

  const start = useCallback(() => {
    if (run.status !== "ready" || !ghostsReady) return;
    ghostSwitched.current.clear();
    setPickerOpen(false);
    setResultView(null);
    run.start();
    setScreen("driving");
  }, [run, ghostsReady]);

  const retry = useCallback(async () => {
    pico.react("retry", { key: `pico-retry-${saveSeq.current}` });
    // 준비(판 상태 초기화)를 먼저 — 끝난 판이 남은 채 '달리기'로 가면 곧바로 결과로 돌아온다
    await run.prepare(presetKey, tracks);
    ghostSwitched.current.clear();
    setResultView(null);
    run.start();
    setScreen("driving");
  }, [run, presetKey, tracks, pico]);

  const openPause = useCallback(() => {
    if (screen !== "driving" || run.run.phase !== "driving") return;
    run.pause();
    setPauseOpen(true);
  }, [run, screen]);

  const driving = screen === "driving" && run.run.phase === "driving" && !pauseOpen;

  const remote = useRemote({
    variant: se ? "se" : "su",
    enabled: driving && movable,
    maxHz: data?.config.input.maxHz ?? 30,
    send: run.input,
    onReset: () => driving && run.reset(),
    onStop: openPause,
  });

  // 개발·E2E·PT-02 계측 창
  useEffect(() => {
    if (!import.meta.env.DEV && !courseDeps().fake) return;
    window.__pfCourse = {
      latency: () => run.probe.summary(),
      hostStats: async () => (run.host() ? run.host()!.stats() : null),
      sender: () => remote.stats,
      view: () => viewRef.current?.stats() ?? null,
      state: () => ({
        screen,
        phase: run.run.phase,
        stalls: run.run.stalls,
        attempts: run.run.attempts,
        realism: data?.play.realism ?? false,
        presetKey,
        failPattern: resultView?.failPattern ?? null,
      }),
    };
  });

  // 계측(perf) — 달리는 동안 fps p5 · 커널 틱 p95
  usePerfTelemetry("course", screen === "driving", async () => {
    const h = run.host();
    return h ? (await h.stats()).tickP95Ms : null;
  });

  // ── 그리기 ──
  if (loadError)
    return (
      <ScreenRoot id="FS-04-01" title={t("course.title")} hideTitle>
        <FeBanner
          code={loadError}
          audience="student"
          actions={[
            { label: t("common.retry"), icon: "⟲", onClick: () => setLoadSeq((n) => n + 1) },
          ]}
        />
      </ScreenRoot>
    );
  if (!data)
    return (
      <ScreenRoot id="FS-04-01" title={t("course.title")} hideTitle>
        <div className="pf-pane" aria-busy="true">
          <PicoBubble text={t("course.loading")} busy />
        </div>
      </ScreenRoot>
    );
  if (data.bundleError)
    return (
      <ScreenRoot id="FS-04-01" title={t("course.title")} hideTitle>
        <BundleProblem
          error={data.bundleError}
          onRetry={() => setLoadSeq((n) => n + 1)}
          onUpload={() => navigate("/upload")}
          onHome={() => navigate("/home")}
          onBench={benchRoot ? () => void openBench() : null}
        />
      </ScreenRoot>
    );
  if (!data.snapshot || !data.bundle)
    return (
      <ScreenRoot id="FS-04-01" title={t("course.title")} hideTitle>
        <div className="pf-pane pf-pane--hero" data-testid="course-nomachine">
          <PicoBubble text={t("course.noMachine")} live={false} />
          <ButtonLink to="/upload" variant="accent" icon="⬆">
            {t("student.home.upload")}
          </ButtonLink>
        </div>
      </ScreenRoot>
    );

  const hostError = run.status === "error";
  // 고른 고스트 중 커널이 받지 못한 것(몸체 없음) — '못 불러옴'으로만(시간 없음)
  const unavailableGhosts =
    run.load && run.status === "ready"
      ? tracks
          .map((g) => ghostOf(g).ghostId)
          .filter((id) => !run.load!.bodies.some((b) => b.owner === id))
      : [];
  const screenId =
    screen === "select" ? "FS-04-01" : screen === "driving" ? "FS-04-02" : "FS-04-03";
  const screenTitle = t(`course.screen.${screen}`);
  const hud = run.hud;
  const cells = progressCells(hud.progress);
  // 4.6 FS-04-02 — 이 코스의 미션 시간 목표(본인 것만 · 남의 시간 · 순위 없음)
  const goalMs = hudGoalMs(mission, COURSE_MODE_ID, presetKey);

  // 오늘의 미션 · 코스 띠 · 고스트 — 고르기 화면은 오른쪽 열(미션 한 줄 + [미션 고르기] → 미션 대화상자 · 지금 코스 한 줄 + [바꾸기] → 코스 대화상자), 결과 화면도 오른쪽 열(코스 줄 → 결과 · 2026-10-09)
  const pickPreset = (k: string) => {
    presetChosen.current = true;
    setPresetKey(k);
  };
  const missionGoals = mission ? mission.goals.filter((g) => g.modeId === COURSE_MODE_ID) : [];
  const missionHere = missionGoals.some((g) => g.presetKey === presetKey);
  const missionCard = mission ? (
    <MissionCard
      mission={mission}
      modeId={COURSE_MODE_ID}
      presets={presets}
      presetKey={presetKey}
      onPreset={(k) => {
        pickPreset(k);
        setMissionPick(false);
      }}
      hideNote
    />
  ) : null;
  const courseStrip = (
    <CourseStrip
      unavailable={unavailableGhosts}
      presets={presets}
      presetKey={presetKey}
      onPreset={(k) => {
        presetChosen.current = true;
        setPresetKey(k);
        if (screen === "result") setScreen("select");
      }}
      ghosts={candidates}
      selected={selected}
      pickerOpen={pickerOpen}
      {...(screen !== "driving"
        ? {
            pick: {
              open: coursePick,
              onOpen: () => setCoursePick(true),
              mission: missionGoals.length ? (missionHere ? "here" : "other") : null,
            } as const,
          }
        : {})}
      onOpenPicker={() => {
        if (screen === "result") setScreen("select");
        setPickerOpen((o) => !o);
      }}
    />
  );
  const ghostPicker = pickerOpen ? (
    <GhostPicker
      ghosts={candidates}
      selected={selected}
      max={maxGhosts}
      onToggle={(id) => setSelected((s) => toggleGhost(s, id, maxGhosts))}
      onClose={() => setPickerOpen(false)}
    />
  ) : null;
  return (
    <ScreenRoot
      id={screenId}
      title={screenTitle}
      hideTitle
      fill
      focusKey={screen}
      className={`pf-course pf-course--${density} pf-course--${screen}`}
    >
      <div className="pf-course__main">
        <div className="pf-course__left">
          {hostError && (
            <FeBanner
              code="NET-SERVER"
              audience="student"
              actions={[
                { label: t("common.retry"), icon: "⟲", onClick: () => window.location.reload() },
              ]}
            />
          )}
          <CourseScene
            run={run}
            load={run.load}
            bundle={data.bundle}
            palette={scenePalette}
            ghosts={tracks.map((g) => {
              const x = ghostOf(g);
              return { id: x.ghostId, label: x.label, bundle: x.bundle };
            })}
            onView={(v) => (viewRef.current = v)}
            principle={{ on: principle, numbers: !se }}
            courseScene={courseSceneOf(presetKey)}
            orbit
          >
            <div
              className="pf-hud pf-hud--timer"
              data-testid="hud-timer"
              aria-label={t(hud.countdown ? "course.hud.timer" : "course.hud.elapsed", {
                time: formatClock(hud.clockS, !se),
              })}
            >
              <span className="pf-hud__timer">{formatClock(hud.clockS, !se)}</span>
            </div>
            {screen === "driving" && (
              <>
                {!se && movable && (
                  <div className="pf-hud pf-hud--keys" data-testid="hud-keys">
                    {t("remote.keys")}
                  </div>
                )}
                <div className="pf-hud-stack">
                  {se ? (
                    <div
                      className="pf-hud"
                      data-testid="hud-progress"
                      aria-label={t("course.hud.progress", { n: cells })}
                    >
                      {/* 칸은 CSS 로 그린다(2026-10-09) — ▮▯ 글자는 Pretendard · 맑은 고딕에 없어 기기마다 대체 글꼴이 그렸고, 빈 칸 ▯ 은 깨진 글자 네모와 똑같이 보였다 */}
                      <span className="pf-hud__cells" aria-hidden="true">
                        🏁
                        {Array.from({ length: 5 }, (_, i) => (
                          <i
                            key={i}
                            className={`pf-hud__cell${i < cells ? " pf-hud__cell--on" : ""}`}
                          />
                        ))}
                      </span>
                    </div>
                  ) : (
                    <div className="pf-hud" data-testid="hud-remaining">
                      {t("course.hud.remaining", { m: (hud.remainingM ?? 0).toFixed(1) })}
                    </div>
                  )}
                  {goalMs != null && (
                    <div
                      className="pf-hud pf-hud--goal"
                      data-testid="hud-goal-time"
                      aria-label={t("course.mission.hudGoalLabel", {
                        time: formatClock(goalMs / 1000, !se),
                      })}
                    >
                      <span aria-hidden="true">
                        🎯 {t("course.mission.hudGoal", { time: formatClock(goalMs / 1000, !se) })}
                      </span>
                    </div>
                  )}
                  {remote.gamepad && (
                    <div className="pf-hud" data-testid="hud-gamepad">
                      {t("remote.gamepad")}
                    </div>
                  )}
                  {run.run.stalls.map((p) => (
                    <div
                      key={p}
                      className="pf-hud pf-hud--stall"
                      role="status"
                      data-testid="hud-stall"
                      data-port={p}
                    >
                      {t(se ? "course.hud.stallSE" : "course.hud.stallSU", { port: p })}
                    </div>
                  ))}
                  <HubHud hub={hubView} now={hubNow} prime={prime} />
                </div>
                {respawnNote && (
                  <div className="pf-hud pf-hud--note" role="status" data-testid="hud-respawn">
                    {t(
                      respawnNote === "fall" ? "course.hud.respawnFall" : "course.hud.respawnReset",
                    )}
                  </div>
                )}
                {!movable && (
                  <div className="pf-hud pf-hud--note" data-testid="hud-static">
                    {t("course.side.static")}
                  </div>
                )}
                {principle && <PrincipleLegend se={se} />}
                {movable && (
                  <RemoteOverlay variant={se ? "se" : "su"} remote={remote} disabled={!driving} />
                )}
                <div
                  className="pf-course__controls"
                  role="group"
                  aria-label={t("course.controls.label")}
                >
                  <Button
                    variant="ghost"
                    icon="⟲"
                    onClick={() => run.reset()}
                    disabled={!driving}
                    data-testid="ctl-reset"
                    aria-keyshortcuts="R"
                  >
                    {t("course.controls.reset")}
                  </Button>
                  <Button
                    variant="ghost"
                    icon="✋"
                    onClick={openPause}
                    disabled={!driving}
                    data-testid="ctl-stop"
                    aria-keyshortcuts="Escape"
                  >
                    {t("course.controls.stop")}
                  </Button>
                  <PrincipleButton on={principleOn} onToggle={() => setPrincipleOn((o) => !o)} />
                </div>
                {pauseOpen && (
                  <PauseDialog
                    onResume={() => {
                      setPauseOpen(false);
                      run.resume();
                    }}
                    onGiveUp={() => {
                      setPauseOpen(false);
                      run.giveUp();
                    }}
                  />
                )}
              </>
            )}
          </CourseScene>
        </div>
        {screen === "select" && (
          <div className="pf-course__right" data-testid="course-right">
            {mission && (
              <MissionSummary
                mission={mission}
                modeId={COURSE_MODE_ID}
                presets={presets}
                presetKey={presetKey}
                open={missionPick}
                onOpen={() => setMissionPick(true)}
              />
            )}
            {courseStrip}
            {ghostPicker}
            <ReadyPane
              glbUrl={glbUrlFor(data.snapshot)}
              appearance={data.snapshot.appearance}
              palette={data.palette}
              ghostCount={Math.max(0, tracks.length - unavailableGhosts.length)}
              staticWork={!movable}
              staticWhy={staticWhy}
              ready={run.status === "ready" && ghostsReady}
              onStart={start}
            />
          </div>
        )}
        {screen === "result" && resultView && (
          <div className="pf-course__right" data-testid="course-right">
            {courseStrip}
            <ResultPane
              view={resultView}
              onRetry={() => void retry()}
              onFixCode={() => {
                if (!openBench()) navigate("/upload");
              }}
              onAssembly={() => navigate("/upload")}
              onGallery={() => navigate("/gallery")}
              onRetrySave={() => void retrySave()}
              onWatch={
                resultView.save.replayId
                  ? () => navigate(`/replay/${encodeURIComponent(resultView.save.replayId!)}`)
                  : null
              }
            />
          </div>
        )}
      </div>
      {coursePick && screen !== "driving" && (
        <CoursePickDialog
          presets={presets}
          presetKey={presetKey}
          onPick={(k) => {
            pickPreset(k);
            setCoursePick(false);
            if (screen === "result") setScreen("select"); // 결과 화면에서 다른 코스 → 고르기 화면(카드 때와 같다)
          }}
          onClose={() => setCoursePick(false)}
        />
      )}
      {missionPick && screen === "select" && missionCard && (
        <MissionPickDialog onClose={() => setMissionPick(false)}>{missionCard}</MissionPickDialog>
      )}
    </ScreenRoot>
  );
}

/**
 * run-bundle 거절 안내(FS-04-01 — 제어 평면 계약 S3):
 *  409 FE-204 스냅샷 뒤 Bench 코드가 바뀜 · 409 FE-201 저장본 없음 → '코딩 고치기(Bench)' · '다시 올리기'
 *  503 FE-201 Bench 연결 안 됨 → '다시 해 보기'(코스 문구 — 다시 올릴 일이 아님) · 403 FE-302 → '입구로'.
 */
function BundleProblem({
  error,
  onRetry,
  onUpload,
  onHome,
  onBench,
}: {
  error: { code: string; status: number };
  onRetry: () => void;
  onUpload: () => void;
  onHome: () => void;
  onBench: (() => void) | null;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const upload = { label: t("course.bundle.reupload"), icon: "⬆", onClick: onUpload };
  const bench = onBench
    ? [
        {
          label: t(se ? "run.action.fixCodeSE" : "run.action.fixCodeSU"),
          icon: "🧩",
          onClick: onBench,
        },
      ]
    : [];
  // 503 FE-201 = Bench 에 닿지 못함 — 다시 올릴 일이 아니라 잠시 뒤 다시(코스 문구)
  if (error.code === "FE-201" && error.status >= 500)
    return (
      <div
        className="pf-banner pf-banner--warn"
        role="status"
        data-fe-code={error.code}
        data-testid="bundle-problem"
      >
        <div className="pf-banner__body">
          <span className="pf-banner__icon" aria-hidden="true">
            💡
          </span>
          <p className="pf-banner__text">
            <b className="pf-banner__cause">{t("course.bundle.benchDown.cause")}</b>{" "}
            <span className="pf-banner__next">{t("course.bundle.benchDown.next")}</span>
          </p>
        </div>
        <div className="pf-banner__actions">
          <Button variant="ghost" icon="⟲" onClick={onRetry}>
            {t("common.retry")}
          </Button>
        </div>
      </div>
    );
  if (!isFeCode(error.code))
    return (
      <div data-testid="bundle-problem" data-fe-code="NET-SERVER">
        <FeBanner
          code="NET-SERVER"
          audience="student"
          actions={[{ label: t("common.retry"), icon: "⟲", onClick: onRetry }]}
        />
      </div>
    );
  const code = error.code as ErrorCode;
  const actions =
    code === "FE-302"
      ? [{ label: t("common.toEntrance"), icon: "🏠", onClick: onHome }]
      : [...bench, upload];
  return (
    <div data-testid="bundle-problem" data-fe-code={code}>
      <FeBanner code={code} audience="student" actions={actions} />
    </div>
  );
}

function PauseDialog({ onResume, onGiveUp }: { onResume: () => void; onGiveUp: () => void }) {
  const { t } = useI18n();
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  const text = t("course.pause.title");
  return (
    <div className="pf-modal" data-testid="pause-dialog">
      <div
        className="pf-pane pf-course__modal-pane"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pause-title"
      >
        <PicoBubble text={text} avatar="🍵" live={false} />
        <h2 id="pause-title" className="pf-sr-only">
          {text}
        </h2>
        <div className="pf-row pf-row--wrap">
          <Button
            ref={first}
            variant="accent"
            icon="▶"
            onClick={onResume}
            data-testid="pause-resume"
          >
            {t("course.pause.resume")}
          </Button>
          <Button variant="ghost" icon="🏁" onClick={onGiveUp} data-testid="pause-giveup">
            {t("course.pause.giveUp")}
          </Button>
        </div>
      </div>
    </div>
  );
}
